import { makeAutoObservable, runInAction } from "mobx";
import EmailContentService, {IEmailCustomization, IEmailCustomizationChange, IEmailCustomizationSettings } from "../../../services/api/email-content-service";
import { TYPE, rootContainer } from "../../../services/container/root";
import {IChangeable, ISaveable, WithOld} from "../../../stores/types";
import currentAccountStore from "../../accounts/stores/current-account-store";

class EmailCustomizationStore implements IChangeable<IEmailCustomization>, ISaveable {
    private _isLoading: boolean = false;
	private _isSaving: boolean = false;
	private _model: IEmailCustomization = {} as IEmailCustomization;
	private _changes: IEmailCustomizationChange = {} ;
	private _settings: IEmailCustomizationSettings = {} as IEmailCustomizationSettings;
	private _logoOriginalDimensions?: string;
	
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

    private get emailContentService(): EmailContentService {
		return rootContainer.get<EmailContentService>(TYPE.EmailContentService);
	}
	public hasChanges(): boolean {
		return !!Object.keys(this._changes).length;
	}

	public get isLoading(): boolean { return this._isLoading || currentAccountStore.isLoading; }

    async load() {
		this._isLoading = true;
		try {
			const emailCustomization = await this.emailContentService.getEmailCustomization();
			currentAccountStore.load();
			return runInAction(() => {
				this._isLoading = false;
				this._setModel(emailCustomization.emailCustomization);
				this._setSettings(emailCustomization.settings);
			});
		} 
		catch (error) {
			runInAction(() => {
                this._isLoading = false;
            });
		}
	}
	
	
	public isSaving(): boolean { return this._isSaving; }

	async save (){
		this._isSaving = true;
        const updatedModel: WithOld<IEmailCustomization> = this.getUpdatedModel();
        updatedModel.__old = this._model;

		try {
			const model = await this.emailContentService.updateEmailCustomization(updatedModel);
			runInAction(() => {
				this._isSaving = false;
				this._setModel(model);
			});
		} catch (error) {
			console.error(error);
			runInAction(() => {
				this._isSaving = false;
			});
		}
		
	}

    private _setModel(model: IEmailCustomization): void {
		this._model = model;
		this.discardChanges();
		this._setLogoOriginalDimensions();
	}
	public isFromAddressDisabled(): boolean {
		return !currentAccountStore.isSysAdmin;
	}
	private get _newLogoBase64Str(): string | undefined {
		if (this._changes._new_logo_data) {
			return `data:${this._changes._new_logo_data?.type};base64,${this._changes._new_logo_data?.base64_content}`;
		}
		return undefined;
	}

    public get logo(): string | undefined {
		if (this._changes._delete_logo) {
			return undefined;
		}
		return this._newLogoBase64Str ?? this._model.logo;
	}
    public get backgroundColor() { return this._changes.bg_color ?? this._model.bg_color; }
    public get textColor() { return this._changes.text_color ?? this._model.text_color; }
    public get linkColor() { return this._changes.link_color ?? this._model.link_color; }
    public get logoAlign() { return this._changes.logo_align ?? this._model.logo_align; }
    public get buttonBackgroundColor() { return this._changes.btn_bg_color ?? this._model.btn_bg_color; }
    public get buttonTextColor() { return this._changes.btn_text_color ?? this._model.btn_text_color; }
	public get senderAddress() {
		return this._changes?.sender_address ?? this._model?.sender_address ?? this._settings.default_sender_address; }
	public get senderName(): string{ return this._settings.sender_name; }
	public get replyToAddress(): string{ return this._settings.reply_to_address; }
	
	public setSenderAddress(value: string) {
        this._changes.sender_address = value;
    }
	
	private _setSettings(settings: IEmailCustomizationSettings) {
		this._settings = settings;
	}
	
	discardChanges(){
		this._changes = {};
	}
	
	public getUpdatedModel():IEmailCustomization  {
		const updatedModel = { ...this._model, ...this._changes };
		return updatedModel
	}

	public get logoOriginalDimensions() {
		return this._logoOriginalDimensions;
	}

	private _setLogoOriginalDimensions() {
		if (!this.logo) {
			this._logoOriginalDimensions = undefined;
			return;
		}
		const img = new Image();
		img.src = this.logo;
		img.onerror = (err) => {
			console.error(err);
		};
		img.onload = () => {
			runInAction(() => {
				this._logoOriginalDimensions = img.width + 'x' + img.height;
			});
		};
	}

	public uploadLogo(type: string, base64_content: string) {
		this._changes._new_logo_data = {
			base64_content,
			type
		};
		delete this._changes._delete_logo;
		this._setLogoOriginalDimensions();
	}

	public deleteLogo() {
		this._changes._delete_logo = true;
		delete this._changes._new_logo_data;
	}

	public setLogoAlign(v: string) {
		this._changes.logo_align = v;
		this._clearUnneededChanges();
	}

	public setBackgroundColor(v: string) {
		this._changes.bg_color = v;
		this._clearUnneededChanges();
	}

	public setButtonBackgroundColor(v: string) {
		this._changes.btn_bg_color = v;
		this._clearUnneededChanges();
	}

	public setButtonTextColor(v: string) {
		this._changes.btn_text_color = v;
		this._clearUnneededChanges();
	}

	public setTextColor(v: string) {
		this._changes.text_color = v;
		this._clearUnneededChanges();
	}

	public setLinkColor(v: string) {
		this._changes.link_color = v;
		this._clearUnneededChanges();
	}

	private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IEmailCustomization;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }
}

export default new EmailCustomizationStore()